import {
  CameraSwitchEvent,
  useActiveCamera,
} from "@/components/r3f/cameras/active-camera-context";
import {
  CameraView,
  CameraViewType,
  setCameraFarPlane,
  SetCameraView,
} from "@/utils/cam-view";
import { useTypedEvent } from "@faro-lotv/app-component-toolbox";
import {
  assert,
  clamp,
  DEFAULT_MAX_PERSP_FOV,
  DEFAULT_MIN_PERSP_FOV,
  DEG_TO_RAD,
  OrientedBoundingBox,
  RAD_TO_DEG,
  WalkOrbitControls,
} from "@faro-lotv/lotv";
import { useCallback, useRef, useState } from "react";
import { Camera, Ray, Sphere, Vector3 } from "three";

type ControlsSetter = (controls: WalkOrbitControls | null) => void;

const DEFAULT_DISTANCE_TO_TARGET = 10.0;

type ManageProjectionSwitchReturnType = {
  setControls: ControlsSetter;
  setAllowOrthoCameraInsideModelOnce(): void;
};

/**
 * Listens to the active camera, on every projection switch event it computes the
 * projection size of the incoming camera based on the projection size of the outgoing
 * and on the current target.
 *
 * @param clippingBox The clipping box that the orthocamera should never enter
 * @param setCameraView A function to set the camera view
 * @returns a function to set the WalkOrbitControls to this hook
 */
export function useManageProjectionSwitch(
  clippingBox: OrientedBoundingBox | undefined,
  setCameraView: SetCameraView,
): ManageProjectionSwitchReturnType {
  const [controls, setControls] = useState<WalkOrbitControls | null>(null);

  const allowOrthoCameraInsideModel = useRef<boolean>(false);

  const setAllowOrthoCameraInsideModelOnce = useCallback(() => {
    allowOrthoCameraInsideModel.current = true;
  }, [allowOrthoCameraInsideModel]);

  const { onOrthoToPerspective, onPerspectiveToOrtho } = useActiveCamera();

  const orthoToPerspectiveSwitch = useCallback(
    (switchEvent: CameraSwitchEvent) => {
      const { orthoCamera, perspectiveCamera } = switchEvent;
      const targetDistance = getTargetDistance(controls, orthoCamera);
      const halfVsize = orthoCamera.top / orthoCamera.zoom;
      const fov = Math.atan(halfVsize / targetDistance) * 2.0 * RAD_TO_DEG;
      perspectiveCamera.fov = clamp(
        fov,
        DEFAULT_MIN_PERSP_FOV,
        DEFAULT_MAX_PERSP_FOV,
      );
      perspectiveCamera.zoom = 1.0;

      // Extending the perspective camera far plane to ensure the inclusion of the bounding box
      if (clippingBox) {
        setCameraFarPlane(
          perspectiveCamera,
          clippingBox.size.length() * 0.5 +
            perspectiveCamera.position.distanceTo(clippingBox.position),
        );
      }
    },
    [controls, clippingBox],
  );

  const perspectiveToOrthoSwitch = useCallback(
    (switchEvent: CameraSwitchEvent) => {
      const targetDistance = getTargetDistance(
        controls,
        switchEvent.orthoCamera,
      );
      const halfVsize =
        targetDistance *
        Math.tan(
          switchEvent.perspectiveCamera.getEffectiveFOV() * DEG_TO_RAD * 0.5,
        );
      const { orthoCamera } = switchEvent;
      orthoCamera.top = halfVsize;
      orthoCamera.bottom = -halfVsize;
      orthoCamera.zoom = 1.0;

      // If the ortho camera is allowed to be placed inside the current bounding box,
      // we early return as the subsequent computation is not needed.
      // Still, this is an exceptional case, therefore we reset the 'allowOrthoCameraInsideModel' flag to false.
      const earlyReturn = allowOrthoCameraInsideModel.current;
      allowOrthoCameraInsideModel.current = false;
      if (earlyReturn) return;

      // If the ortho camera is inside the box's bounding sphere, then it is animated outside
      // to not clip any geometry.
      const sphere = boxToSphere(clippingBox);
      if (sphere?.containsPoint(orthoCamera.position)) {
        const ray = new Ray();
        ray.origin.copy(orthoCamera.position);
        orthoCamera.updateMatrixWorld(true);
        const { elements } = orthoCamera.matrixWorld;
        ray.direction.set(elements[8], elements[9], elements[10]);

        const intersection = ray.intersectSphere(sphere, new Vector3());
        assert(intersection, "The ray must intersect the sphere");

        // Extending the orthocamera far plane to ensure the inclusion of the bounding box
        setCameraFarPlane(
          orthoCamera,
          intersection.distanceTo(sphere.center) + sphere.radius,
        );

        const cameraView: CameraView = {
          position: intersection,
          rotation: orthoCamera.quaternion.clone(),
          type: CameraViewType.orthographic,
          zoomLevel: sphere.radius * 2,
          target: controls ? controls.target?.clone() : undefined,
        };
        setCameraView(cameraView);
      }
    },
    [controls, clippingBox, setCameraView, allowOrthoCameraInsideModel],
  );

  useTypedEvent(onOrthoToPerspective, orthoToPerspectiveSwitch);
  useTypedEvent(onPerspectiveToOrtho, perspectiveToOrthoSwitch);

  return {
    setControls,
    setAllowOrthoCameraInsideModelOnce,
  };
}

function getTargetDistance(
  controls: WalkOrbitControls | null,
  camera: Camera,
): number {
  let targetDistance = DEFAULT_DISTANCE_TO_TARGET;
  if (controls?.target) {
    targetDistance = camera.position.distanceTo(controls.target);
  }
  return targetDistance;
}

const SPHERE = new Sphere();

function boxToSphere(
  clippingBox: OrientedBoundingBox | undefined,
): Sphere | undefined {
  if (clippingBox) {
    SPHERE.center.copy(clippingBox.position);
    SPHERE.radius = clippingBox.size.length() * 0.5;
    return SPHERE;
  }
}
